import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Building2, MapPin, User, Shield } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';

const CLIENT_CLAUSES = [
  { id: 'service_terms', title: 'Service Terms', text: 'You agree to use the platform for legitimate freight and logistics operations only. All loads submitted must contain accurate information.' },
  { id: 'pricing', title: 'Pricing & Payment', text: 'Rates are calculated based on distance, weight, and equipment type. You agree to pay all invoiced amounts within the agreed payment terms.' },
  { id: 'liability', title: 'Liability & Insurance', text: 'Cargo insurance requirements must be communicated at the time of load creation. The platform facilitates the connection between shippers and carriers.' },
  { id: 'data_accuracy', title: 'Data Accuracy', text: 'All shipment details including weight, dimensions, and special handling requirements must be accurately provided. Discrepancies may result in additional charges.' },
  { id: 'communication', title: 'Communication Standards', text: 'Professional and timely communication is required. Respond to driver inquiries and delivery confirmations promptly.' },
];

const STEPS = ['Terms', 'Company', 'Contact', 'Locations', 'Complete'];

export default function ClientOnboarding() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState({});
  const [form, setForm] = useState({
    company_name: user?.company_name || '', company_address: '', company_industry: '',
    company_tax_id: '', company_website: '',
    name: user?.name || '', phone: user?.phone || '',
    address_street: '', address_city: '', address_province: '', address_postal: '',
    billing_address_street: '', billing_address_city: '', billing_address_province: '', billing_address_postal: '',
  });

  const allAccepted = CLIENT_CLAUSES.every(c => accepted[c.id]);

  const handleAcceptTerms = async () => {
    if (!allAccepted) { toast.error('Please accept all terms'); return; }
    setLoading(true);
    try {
      await api.post('/terms/accept', {
        version: '1.0', clause_acceptances: CLIENT_CLAUSES.map(c => ({ clause_id: c.id, accepted: true })),
      });
      setStep(1);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  };

  const handleSaveProfile = async () => {
    if (!form.company_name) { toast.error('Company name is required'); return; }
    setLoading(true);
    try {
      await api.put('/onboarding/profile', form);
      setStep(step + 1);
    } catch { toast.error('Failed to save'); } finally { setLoading(false); }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await api.get('/onboarding/status');
      toast.success('Onboarding complete! Welcome to TORQ.');
      navigate('/client');
      window.location.reload();
    } catch { navigate('/client'); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <BrandLogo size="lg" />
          <h1 className="text-2xl font-bold mt-4">Welcome to TORQ</h1>
          <p className="text-muted-foreground mt-2">Let's set up your shipper account</p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>

        {/* Step 0: Terms */}
        {step === 0 && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Terms & Conditions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {CLIENT_CLAUSES.map(c => (
                <div key={c.id} className={`p-4 rounded-xl border cursor-pointer transition-colors ${accepted[c.id] ? 'border-emerald-500 bg-emerald-50' : 'border-border'}`}
                  onClick={() => setAccepted(prev => ({ ...prev, [c.id]: !prev[c.id] }))}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${accepted[c.id] ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground'}`}>
                      {accepted[c.id] && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="font-medium text-sm">{c.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 ml-8">{c.text}</p>
                </div>
              ))}
              <Button className="w-full mt-4" onClick={handleAcceptTerms} disabled={!allAccepted || loading}>
                {loading ? 'Saving...' : 'I Accept All Terms'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Company */}
        {step === 1 && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" /> Company Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Company Name *</Label><Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="Your company name" className="mt-1" /></div>
              <div><Label>Industry</Label><Input value={form.company_industry} onChange={e => setForm({ ...form, company_industry: e.target.value })} placeholder="e.g. Manufacturing, Retail" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Tax ID / BN</Label><Input value={form.company_tax_id} onChange={e => setForm({ ...form, company_tax_id: e.target.value })} placeholder="Business Number" className="mt-1" /></div>
                <div><Label>Website</Label><Input value={form.company_website} onChange={e => setForm({ ...form, company_website: e.target.value })} placeholder="https://" className="mt-1" /></div>
              </div>
              <div><Label>Company Address</Label><Input value={form.company_address} onChange={e => setForm({ ...form, company_address: e.target.value })} placeholder="Full address" className="mt-1" /></div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1">Back</Button>
                <Button onClick={handleSaveProfile} disabled={loading} className="flex-[2]">{loading ? 'Saving...' : 'Next'}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Contact */}
        {step === 2 && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Contact Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Contact Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Street Address</Label><Input value={form.address_street} onChange={e => setForm({ ...form, address_street: e.target.value })} className="mt-1" /></div>
                <div><Label>City</Label><Input value={form.address_city} onChange={e => setForm({ ...form, address_city: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Province</Label><Input value={form.address_province} onChange={e => setForm({ ...form, address_province: e.target.value })} className="mt-1" /></div>
                <div><Label>Postal Code</Label><Input value={form.address_postal} onChange={e => setForm({ ...form, address_postal: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
                <Button onClick={handleSaveProfile} disabled={loading} className="flex-[2]">{loading ? 'Saving...' : 'Next'}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Billing */}
        {step === 3 && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> Billing Address</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Optional — used for invoicing</p>
              <div><Label>Street</Label><Input value={form.billing_address_street} onChange={e => setForm({ ...form, billing_address_street: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>City</Label><Input value={form.billing_address_city} onChange={e => setForm({ ...form, billing_address_city: e.target.value })} className="mt-1" /></div>
                <div><Label>Province</Label><Input value={form.billing_address_province} onChange={e => setForm({ ...form, billing_address_province: e.target.value })} className="mt-1" /></div>
                <div><Label>Postal</Label><Input value={form.billing_address_postal} onChange={e => setForm({ ...form, billing_address_postal: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
                <Button onClick={() => { handleSaveProfile(); }} disabled={loading} className="flex-[2]">{loading ? 'Saving...' : 'Complete Setup'}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Complete */}
        {step === 4 && (
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold">Setup Complete!</h2>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                Your account is being verified. You'll receive a notification once approved. In the meantime, you can explore the dashboard.
              </p>
              <Button className="mt-6" onClick={handleComplete} disabled={loading}>
                {loading ? 'Loading...' : 'Go to Dashboard'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
